/**
 * Builds the rows of the "export volunteers" spreadsheet an organization downloads for one
 * event: one row per accepted volunteer with their name, age, departments, shifts and
 * t-shirt size. Pure, so the Supabase reads live in `exportEventVolunteers.ts`.
 */

import { calculateAge } from '@/utils/age';
import { uniqueDepartments } from '@/utils/departments';
import {
  compareShifts,
  formatShiftLabel,
  type EventShift,
} from '@/utils/eventShifts';
import { deriveFileName } from '@/utils/saveFile';

/** An active `volunteers` row. */
export interface ExportVolunteerRow {
  volunteer_id: string | null;
  department: string | null;
}

/** The `profiles` columns the export reads. */
export interface ExportProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  /** Stored at save time, so it can be stale; only used when there is no birth date. */
  age: number | string | null;
  tshirtSize: string | null;
}

export interface EventExportRow {
  name: string;
  age: number | null;
  department: string;
  /** One shift per line. */
  shifts: string;
  tshirtSize: string;
}

export interface BuildEventExportRowsInput {
  volunteers: ExportVolunteerRow[];
  profilesById: Record<string, ExportProfile>;
  shifts: Pick<EventShift, 'id' | 'starts_at' | 'ends_at'>[];
  /** `{ shiftId: volunteerIds }`, as returned by `fetchEventShiftRoster`. */
  roster: Record<string, string[]>;
  now?: Date;
}

function exportAge(
  profile: ExportProfile | undefined,
  now: Date
): number | null {
  if (!profile) return null;
  const fromBirth = profile.date_of_birth
    ? calculateAge(profile.date_of_birth, now)
    : null;
  if (fromBirth != null && fromBirth >= 0) return fromBirth;
  const stored =
    profile.age == null || profile.age === '' ? NaN : Number(profile.age);
  return Number.isFinite(stored) ? stored : null;
}

export function buildEventExportRows({
  volunteers,
  profilesById,
  shifts,
  roster,
  now = new Date(),
}: BuildEventExportRowsInput): EventExportRow[] {
  // A volunteer can have more than one active row on an event; merge them into one.
  const departmentsByVolunteer = new Map<string, (string | null)[]>();
  for (const row of volunteers) {
    if (!row.volunteer_id) continue;
    const raws = departmentsByVolunteer.get(row.volunteer_id);
    if (raws) raws.push(row.department);
    else departmentsByVolunteer.set(row.volunteer_id, [row.department]);
  }

  const shiftsById = new Map(shifts.map((shift) => [shift.id, shift]));
  const shiftsByVolunteer = new Map<
    string,
    Pick<EventShift, 'starts_at' | 'ends_at'>[]
  >();
  for (const [shiftId, volunteerIds] of Object.entries(roster)) {
    const shift = shiftsById.get(shiftId);
    if (!shift) continue;
    for (const volunteerId of volunteerIds) {
      const list = shiftsByVolunteer.get(volunteerId);
      if (list) list.push(shift);
      else shiftsByVolunteer.set(volunteerId, [shift]);
    }
  }

  const rows = [...departmentsByVolunteer.entries()].map(
    ([volunteerId, rawDepartments]) => {
      const profile = profilesById[volunteerId];
      const name = [profile?.first_name, profile?.last_name]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');
      return {
        name: name || 'Volunteer',
        age: exportAge(profile, now),
        department: uniqueDepartments(rawDepartments).join(', '),
        shifts: [...(shiftsByVolunteer.get(volunteerId) ?? [])]
          .sort(compareShifts)
          .map(formatShiftLabel)
          .join('\n'),
        tshirtSize: profile?.tshirtSize?.trim() ?? '',
      };
    }
  );

  return rows.sort((a, b) => a.name.localeCompare(b.name));
}

/** 'Beach Cleanup' → 'Beach-Cleanup-volunteers.xlsx' */
export function buildExportFileName(
  eventName: string | null | undefined
): string {
  const base = eventName?.trim()
    ? `${eventName.trim()}-volunteers`
    : 'volunteers';
  return deriveFileName('', base, 'xlsx');
}
